import {
  Loader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@trackman/web-shared-components';
import React, { FC, useEffect } from 'react';

import { AppSectionErrorBoundary } from 'components/ui';
import { getColumnFilter } from 'components/TableFilters/StaticTableFilters';
import { t } from 'utilities/TranslationUtils';
import { tableHooks } from './WSC-v2/tableHooks';

interface STProps {
  columns: any[];
  data: any[];
  rowMapper?: (cells: any[]) => JSX.Element;
  rowsPerPage: number;
  FiltersComponent?: any;
  noResultMessage?: string;
  isLoading?: boolean;
  hideFooter?: boolean;
  hideFilters?: boolean;
}

export const DataTable: FC<STProps> = (props: STProps) => {
  const {
    columns,
    data,
    rowMapper,
    rowsPerPage,
    noResultMessage,
    isLoading,
    FiltersComponent,
    hideFooter = false,
    hideFilters = false,
  } = props;
  const { useTable, useSortBy, usePagination, useFlexLayout } = tableHooks;

  const [pageLocal, setPageLocal] = React.useState(1);

  const onChangePageLocal = (pageLocal: number) => {
    setPageLocal(pageLocal);
  };

  useEffect(() => {
    setPageLocal(1);
  }, [data]);

  const dataMemorized = React.useMemo(() => {
    return data?.slice((pageLocal - 1) * rowsPerPage, pageLocal * rowsPerPage);
  }, [data, pageLocal, rowsPerPage]);

  const { getTableProps, rows, headerGroups, prepareRow } = useTable(
    {
      columns: columns,
      data: dataMemorized,
    },
    useSortBy,
    usePagination,
    useFlexLayout
  );

  // const getColumnsFilters = (index: number) => ColumnItems(columns.map((item) => item?.filter)?.filter(Boolean))?.[index];

  return (
    <AppSectionErrorBoundary>
      <TableContainer>
        <>{!hideFilters && <div>{FiltersComponent}</div>}</>

        <Table className='data-table' {...getTableProps()}>
          <TableHead>
            {headerGroups.map(({ headers, getHeaderGroupProps }) => (
              <TableRow {...getHeaderGroupProps()}>
                {headers.map(({ sortable, isSorted, isSortedDesc, align, render, getHeaderProps, getSortByToggleProps }, index) => {
                  return (
                    <TableCell
                      {...getHeaderProps(sortable && getSortByToggleProps())}
                      sortable={sortable}
                      sortDirection={isSorted ? (isSortedDesc ? 'desc' : 'asc') : false}
                      align={align}
                    >
                      <div>
                        {render('Header')}
                        {!hideFilters && columns[index]?.filter ? getColumnFilter(columns[index].filter) : <></>}
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {isLoading ? (
              <>
                {/* // 560px is the height of the table when there are 10 rows */}
                <tr style={{ height: '560px' }}>
                  <td>
                    <div className='w-100 d-flex align-items-center justify-content-center p-4'>
                      <Loader size='large' />
                    </div>
                  </td>
                </tr>
              </>
            ) : (
              <>
                {rows?.length > 0 ? (
                  <>
                    {rows.map((row, index) => {
                      prepareRow(row);
                      return (
                        <TableRow index={index} {...row.getRowProps()}>
                          {rowMapper
                            ? rowMapper(row.cells)
                            : row.cells.map(({ getCellProps, column, render }) => (
                                <TableCell {...getCellProps()} align={column.align}>
                                  {render('Cell')}
                                </TableCell>
                              ))}
                        </TableRow>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {/* // 560px is the height of the table when there are 10 rows */}
                    <tr style={{ height: '560px' }}>
                      <td colSpan={columns.length} style={{ textAlign: 'center' }}>
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          {t('table.no_data')}
                        </div>
                      </td>
                    </tr>
                  </>
                )}
              </>
            )}
          </TableBody>
          {hideFooter || data.length === 0 ? (
            <></>
          ) : (
            <TableFooter>
              <TableRow>
                <TablePagination
                  count={pageLocal * rows.length + 1} // this component does not support server side pagination, hence + 1 at the end is needed
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[rowsPerPage]}
                  onRowsPerPageChange={undefined}
                  page={pageLocal - 1} // In old table component, page is 1 based, in new one it is 0 based
                  onPageChange={(_, p) => onChangePageLocal(p + 1)}
                  renderPages={() => (
                    <Typography>
                      Page {pageLocal} of {Math.ceil(data.length / rowsPerPage)}
                    </Typography>
                  )}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </AppSectionErrorBoundary>
  );
};
